import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAxios } from '../../Helpers/AxiosHelper';
import { Card, Column, DataTable, Dialog, ProgressBar } from '../../Common/Imports';
import { ApiResponse, Reservations_EXT } from '../../Models/Classes';
import { FormatDateTimeFromDate } from '../../Common/Utility/FormatDateTimeFromDate';

interface ReservationDetailsProps {
    IDReservation?: number | undefined;
    OnHide: () => void;
    Reservation?: Reservations_EXT | undefined;
}

export const ReservationDetail: React.FC<ReservationDetailsProps> = ({ IDReservation, OnHide, Reservation }) => {
    const AxiosHelper = useAxios();
    const { t } = useTranslation();
    const [Data, SetData] = useState<Reservations_EXT | undefined>(Reservation);
    const [DataLoader, SetDataLoader] = useState<boolean>(true);

    const GetData = useCallback(async () => {
        SetDataLoader(true);
        try {
            const resp = await AxiosHelper.get<ApiResponse<Reservations_EXT>>(`Reservation/Get/${IDReservation}`);
            SetData(resp.data.data);
        } catch (error) {
            console.error("Error fetching stakeholder data:", error);
        } finally {
            SetDataLoader(false);
        }
    }, [AxiosHelper, IDReservation]);

    useEffect(() => {
        if (IDReservation) {
            GetData();
        } else {
            SetDataLoader(false);
        }
    }, [GetData, IDReservation]);

    return (
        <Dialog
            visible={true}
            onHide={OnHide}
            style={{ minWidth: "80rem", minHeight: "40rem" }}
            header={"Reservation " + Data?.idReservation + " - " + Data?.providerName}
        >
            {
                DataLoader ?
                    <ProgressBar mode="indeterminate" style={{ height: '6px' }}></ProgressBar>
                    :
                    <>
                        <div className="row mb-2">
                            <div className="col-md-6">
                                <Card title={t("ReservationDetail.Reservation.Title")} className="h-100">
                                    <p><strong>{t("ReservationDetail.Reservation.bookingDate")}:</strong> {Data?.bookingDate ? FormatDateTimeFromDate(new Date(Data.bookingDate)) : "N/A"}</p>
                                    <p><strong>{t("ReservationDetail.Reservation.checkInDate")}:</strong> {Data?.checkInDate ? FormatDateTimeFromDate(new Date(Data.checkInDate)) : "N/A"}</p>
                                    <p><strong>{t("ReservationDetail.Reservation.checkOutDate")}:</strong> {Data?.checkOutDate ? FormatDateTimeFromDate(new Date(Data.checkOutDate)) : "N/A"}</p>
                                    <p><strong>{t("ReservationDetail.Reservation.customerReservationCode")}:</strong> {Data?.customerReservationCode || "N/A"}</p>
                                    <p><strong>{t("ReservationDetail.Reservation.roomName")}:</strong> {Data?.roomName || "N/A"}</p>
                                    <p><strong>{t("ReservationDetail.Reservation.mealPlan")}:</strong> {Data?.mealPlan || "N/A"}</p>
                                </Card>
                            </div>
                            <div className="col-md-6">
                                <Card title={t("ReservationDetail.Property.Title")} className="h-100">
                                    <p><strong>{t("ReservationDetail.Property.propertyName")}:</strong> {Data?.propertyName || "N/A"}</p>
                                    <p><strong>{t("ReservationDetail.Property.propertyAddress")}:</strong> {Data?.propertyAddress || "N/A"}</p>
                                    <p><strong>{t("ReservationDetail.Property.propertyCountry")}:</strong> {Data?.propertyCountry || "N/A"}</p>
                                    <p><strong>{t("ReservationDetail.Property.propertyISOCode")}:</strong> {Data?.propertyISOCode || "N/A"}</p>
                                    <p><strong>{t("ReservationDetail.Property.propertyEmail")}:</strong> {Data?.propertyEmail || "N/A"}</p>
                                    <DataTable
                                        paginator
                                        rows={10}
                                        rowsPerPageOptions={[10, 20, 50]}
                                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} records"
                                        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                                        size="small"
                                        stripedRows
                                        value={Data?.property.mappings}>
                                        <Column
                                            header={t("ReservationDetail.Property.CIN")}
                                            field="cin"
                                            bodyClassName="table-cell-nowrap" />
                                        <Column
                                            header={t("ReservationDetail.Property.GIATA")}
                                            field="giata"
                                            bodyClassName="table-cell-nowrap" />
                                        <Column
                                            header={t("ReservationDetail.Property.idPropertyProvider")}
                                            field="idPropertyProvider"
                                            bodyClassName="table-cell-nowrap" />
                                        <Column
                                            header={t("ReservationDetail.Property.idProvider")}
                                            field="idProvider"
                                            bodyClassName="table-cell-nowrap" />
                                    </DataTable>
                                </Card>
                            </div>
                        </div>

                        <div className="row">
                            {
                                Data?.paxList && Data.paxList.length > 0 ? (
                                    Data.paxList.map((pax) => (
                                        <div className="col-md-4 mb-3">
                                            <Card title={("Pax " + pax.paxIndex + ": " + pax.name + " " + pax.surname)}>
                                                <p><strong>{t("ReservationDetail.Pax.gender")}:</strong> {pax.gender || "N/A"}</p>
                                                <p><strong>{t("ReservationDetail.Pax.dateOfBirth")}:</strong> {pax.dateOfBirth ? FormatDateTimeFromDate(new Date(pax.dateOfBirth)) : "N/A"}</p>
                                            </Card>
                                        </div>
                                    ))
                                ) : (
                                    <p>{t("ReservationDetail.Pax.NoPassengers")}</p>
                                )
                            }
                        </div>
                    </>
            }
        </Dialog >
    );
};

export default ReservationDetail;
